import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { api } from './services/api';
import { GarageDetailResponse } from './types';
import Layout from './components/Layout';
import AccessDenied from './components/AccessDenied';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import WelcomePage from './pages/WelcomePage';
import OnboardingLayout from './components/onboarding/OnboardingLayout';
import LicenseAndIdStep from './pages/onboarding/LicenseAndIdStep';
import GarageInfoStep from './pages/onboarding/GarageInfoStep';
import TermsStep from './pages/onboarding/TermsStep';
import SuccessPage from './pages/onboarding/SuccessPage';
import PendingApprovalPage from './pages/onboarding/PendingApprovalPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import BuildServicesCataloguePage from './pages/BuildServicesCataloguePage';
import GarageProfilePage from './pages/GarageProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import MyProfilePage from './pages/MyProfilePage';
import OrdersPage from './pages/OrdersPage';
import EarningsPage from './pages/EarningsPage';
import PartnerSettlementDetailPage from './pages/PartnerSettlementDetailPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ReviewsPage from './pages/ReviewsPage';
import PromotionsPage from './pages/PromotionsPage';
import OfferEditorPage from './pages/OfferEditorPage';
import PackageEditorPage from './pages/PackageEditorPage';
import DealerLoginPage from './pages/dealer/DealerLoginPage';
import DealerRoutes from './pages/dealer/DealerRoutes';
import { dealerOrigin, isDealerHost } from './utils/portalHost';

function NoGarageAssigned({ auth }: { auth: ReturnType<typeof useAuth> }) {
  return (
    <Layout auth={auth}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.75rem' }}>No Garage Assigned</h2>
          <p style={{ color: '#6b7280', fontSize: '1rem', maxWidth: '400px' }}>
            You are registered as a garage team member, but you have not been assigned to a garage yet.
            Please contact the garage owner to add you.
          </p>
        </div>
      </div>
    </Layout>
  );
}

function OnboardingGuard({ auth, children }: { auth: ReturnType<typeof useAuth>; children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [noGarage, setNoGarage] = useState(false);

  const isOwner = auth.user?.roles.includes('GARAGE_OWNER') ?? false;
  const isManager = auth.user?.roles.includes('GARAGE_MANAGER') ?? false;
  const isSupport = auth.user?.roles.includes('GARAGE_SUPPORT') ?? false;
  // Manager and Support are non-owner team members — both belong to an existing
  // approved garage and should never see the onboarding wizard. They have full
  // operational access (gated by the backend), the routing here is purely UX.
  const isNonOwnerStaff = (isManager || isSupport) && !isOwner;

  useEffect(() => {
    if (!auth.token) {
      setChecking(false);
      return;
    }

    // Non-owner team members (manager/support) should never access onboarding routes
    if (isNonOwnerStaff && location.pathname.startsWith('/onboarding')) {
      navigate('/dashboard', { replace: true });
      setChecking(false);
      return;
    }

    api.getMyGarage(auth.token).then(async (garage: GarageDetailResponse) => {
      if (isNonOwnerStaff) {
        // Manager/Support: only go to dashboard if garage is APPROVED
        if (garage.status === 'APPROVED') {
          const allowedRoutes = ['/dashboard', '/services', '/build-services', '/profile', '/settings', '/me', '/orders', '/reviews', '/earnings', '/promotions'];
          if (!allowedRoutes.some(r => location.pathname.startsWith(r))) {
            navigate('/dashboard', { replace: true });
          }
        } else {
          // Garage exists but not approved — show "no garage" state
          setNoGarage(true);
        }
      } else {
        // GARAGE_OWNER routing
        if (garage.status === 'DRAFT') {
          if (!location.pathname.startsWith('/onboarding')) {
            navigate('/onboarding/license-and-id', { replace: true });
          }
        } else if (garage.status === 'PENDING' || garage.status === 'UPDATE_REQUESTED' || garage.status === 'REJECTED') {
          if (!location.pathname.startsWith('/pending-approval')) {
            navigate('/pending-approval', { replace: true });
          }
        } else if (garage.status === 'APPROVED') {
          const approvedRoutes = ['/dashboard', '/services', '/build-services', '/profile', '/settings', '/me', '/orders', '/reviews', '/earnings', '/promotions'];
          if (!approvedRoutes.some(r => location.pathname.startsWith(r))) {
            // Check if garage has services; if not, redirect to build-services page
            try {
              const hasServicesResult = await api.getMyHasServices(auth.token!);
              if (!hasServicesResult.hasServices && location.pathname === '/dashboard') {
                navigate('/build-services', { replace: true });
              } else {
                navigate('/dashboard', { replace: true });
              }
            } catch {
              navigate('/dashboard', { replace: true });
            }
          }
        }
      }
    }).catch(() => {
      if (isNonOwnerStaff) {
        // Manager/Support with no garage — show "not assigned" page
        setNoGarage(true);
      } else {
        // OWNER with no garage — start onboarding
        if (!location.pathname.startsWith('/onboarding')) {
          navigate('/onboarding/license-and-id', { replace: true });
        }
      }
    }).finally(() => setChecking(false));
  }, [auth.token, navigate, location.pathname, isOwner, isNonOwnerStaff]);

  if (checking) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (noGarage) {
    return <NoGarageAssigned auth={auth} />;
  }

  return <>{children}</>;
}

function App() {
  const auth = useAuth();
  const location = useLocation();

  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (auth.isAccessDenied) {
    return <AccessDenied onLogout={auth.logout} />;
  }

  // Spec 085: dealer accounts live under /dealer/*; every garage route sends them to the dealer dashboard
  // (garage accounts are sent away from /dealer/* by DealerRoutes).
  if (auth.isAuthenticated && auth.isDealer && !location.pathname.startsWith('/dealer')) {
    return <Navigate to="/dealer/dashboard" replace />;
  }

  // Spec 085 (dealer portal on its own domain): the dealer host offers only the dealer portal, and the
  // partner host hands /dealer/* over to the dealer host (hosts without a pair keep both by path).
  if (isDealerHost() && !location.pathname.startsWith('/dealer')) {
    return <Navigate to={auth.isAuthenticated ? '/dealer/dashboard' : '/dealer/login'} replace />;
  }
  const dealerHostOrigin = dealerOrigin();
  if (dealerHostOrigin && location.pathname.startsWith('/dealer')) {
    window.location.replace(`${dealerHostOrigin}${location.pathname}${location.search}`);
    return null;
  }

  return (
    <>
      <Routes>
      <Route path="/" element={auth.isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} />
      <Route path="/login" element={auth.isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage auth={auth} />} />

      {/* Spec 085 — dealer portal: its own login (X-Garagat-Client: dealer-portal) and screens */}
      <Route
        path="/dealer/login"
        element={auth.isAuthenticated
          ? <Navigate to={auth.isDealer ? '/dealer/dashboard' : '/dashboard'} replace />
          : <DealerLoginPage auth={auth} />}
      />
      <Route path="/dealer/*" element={<DealerRoutes auth={auth} />} />

      {/* Onboarding wizard routes — guard redirects non-DRAFT users away */}
      <Route
        path="/onboarding"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <OnboardingLayout />
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      >
        <Route index element={<Navigate to="license-and-id" replace />} />
        <Route path="license-and-id" element={<LicenseAndIdStep />} />
        <Route path="trade-license" element={<Navigate to="../license-and-id" replace />} />
        <Route path="garage-info" element={<GarageInfoStep />} />
        <Route path="terms" element={<TermsStep />} />
        <Route path="success" element={<SuccessPage />} />
      </Route>

      <Route
        path="/pending-approval"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <PendingApprovalPage auth={auth} />
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/welcome"
        element={
          auth.isAuthenticated
            ? <WelcomePage auth={auth} />
            : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/dashboard"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <DashboardPage auth={auth} />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/services"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <ServicesPage auth={auth} />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/services/:serviceId"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <ServiceDetailPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/build-services"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <BuildServicesCataloguePage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/profile"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <GarageProfilePage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/settings"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <SettingsPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/me"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <MyProfilePage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/orders"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <OrdersPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <OrderDetailPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/earnings"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <EarningsPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/earnings/:id"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <PartnerSettlementDetailPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/reviews"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <ReviewsPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      {/* Spec 062 — garage offers. /promotions/new and /promotions/:id share the editor;
          one route keeps editor state when a new draft gets its id. */}
      <Route
        path="/promotions"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <PromotionsPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      {/* Spec 081b — packages. Kept before /promotions/:id; "new" and ":id" share one route (like offers)
          so the editor keeps its state when a new draft gets its id. */}
      <Route path="/promotions/packages" element={<Navigate to="/promotions?tab=packages" replace />} />
      <Route
        path="/promotions/packages/:id"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <PackageEditorPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/promotions/:id"
        element={
          auth.isAuthenticated
            ? (
              <OnboardingGuard auth={auth}>
                <Layout auth={auth}>
                  <OfferEditorPage />
                </Layout>
              </OnboardingGuard>
            )
            : <Navigate to="/login" replace />
        }
      />
    </Routes>
    </>
  );
}

export default App;
